import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Shield, ExternalLink, X } from 'lucide-react';

const STORAGE_KEY = 'bosswin_terms_accepted';

export default function TermsConsent() {
  const [showModal, setShowModal] = useState(false);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    const accepted = localStorage.getItem(STORAGE_KEY);
    if (!accepted) setShowModal(true);
  }, []);

  const handleAccept = () => {
    localStorage.setItem(STORAGE_KEY, Date.now().toString());
    setShowModal(false);
    setShowBanner(false);
  };

  const handleDismiss = () => {
    setShowModal(false);
    setShowBanner(true);
  };

  if (!showModal && !showBanner) return null;

  // Persistent banner when dismissed without accepting
  if (!showModal && showBanner) {
    return (
      <div
        className="fixed bottom-0 inset-x-0 z-50 bg-gray-900 border-t border-gray-700 px-4 py-3 flex items-center justify-between gap-3 animate-in slide-in-from-bottom duration-300"
        data-testid="terms-banner"
      >
        <p className="text-sm text-gray-300 flex-1">
          By using this site you agree to our{' '}
          <Link to="/terms" className="text-[#EAB308] underline hover:text-yellow-300">
            Terms & Conditions
          </Link>.
        </p>
        <button
          onClick={handleAccept}
          className="shrink-0 bg-[#EAB308] text-gray-900 text-sm font-semibold px-4 py-1.5 rounded-full hover:bg-yellow-400 transition-colors"
          data-testid="terms-banner-accept"
        >
          Accept
        </button>
        <button
          onClick={() => setShowBanner(false)}
          className="shrink-0 text-gray-500 hover:text-gray-300 transition-colors"
          data-testid="terms-banner-close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  // Full-screen modal on first visit
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="terms-modal-overlay">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Modal */}
      <div
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-auto overflow-hidden animate-in zoom-in-95 fade-in duration-300"
        data-testid="terms-modal"
      >
        {/* Header */}
        <div className="bg-gray-900 px-6 py-5 flex items-center gap-3">
          <div className="bg-[#EAB308]/20 p-2 rounded-xl">
            <Shield className="w-6 h-6 text-[#EAB308]" />
          </div>
          <div>
            <h2 className="text-white font-bold text-lg">Terms & Conditions</h2>
            <p className="text-gray-400 text-sm">Please review before continuing</p>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-5 space-y-4 max-h-[50vh] overflow-y-auto">
          <p className="text-gray-700 text-sm leading-relaxed">
            Welcome to <strong>Boss Win</strong>. By accessing or using our website, you acknowledge that you have read, understood, and agree to be bound by these terms.
          </p>

          <div className="space-y-3">
            <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
              <span className="text-[#EAB308] font-bold text-sm mt-0.5">18+</span>
              <p className="text-gray-600 text-sm">You must be 18 years or older and a UK resident to participate.</p>
            </div>
            <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
              <span className="text-[#EAB308] font-bold text-sm mt-0.5">Free</span>
              <p className="text-gray-600 text-sm">No purchase necessary to enter. Free and paid entries carry equal weight.</p>
            </div>
            <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
              <span className="text-[#EAB308] font-bold text-sm mt-0.5">Fair</span>
              <p className="text-gray-600 text-sm">Winners are selected using a verifiable random method. Draw proofs are published for transparency.</p>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Link
              to="/terms"
              className="inline-flex items-center gap-1.5 text-sm text-[#EAB308] hover:text-yellow-600 font-medium transition-colors"
              data-testid="terms-modal-read-full"
            >
              Read full Terms & Conditions
              <ExternalLink className="w-3.5 h-3.5" />
            </Link>
            <Link
              to="/privacy"
              className="inline-flex items-center gap-1.5 text-sm text-[#EAB308] hover:text-yellow-600 font-medium transition-colors"
              data-testid="terms-modal-read-privacy"
            >
              Read Privacy Policy
              <ExternalLink className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleAccept}
            className="flex-1 bg-[#EAB308] text-gray-900 font-bold py-3 rounded-xl hover:bg-yellow-400 transition-colors text-sm"
            data-testid="terms-modal-accept"
          >
            I Accept
          </button>
          <button
            onClick={handleDismiss}
            className="flex-1 bg-gray-200 text-gray-600 font-medium py-3 rounded-xl hover:bg-gray-300 transition-colors text-sm"
            data-testid="terms-modal-dismiss"
          >
            Maybe Later
          </button>
        </div>
      </div>
    </div>
  );
}
